import type { DailyBanner, ProjectTask } from '../types';

interface Props {
  userName: string;
  banner: DailyBanner | null;
  projectTasks: ProjectTask[];
  checklistDone: number;
  checklistTotal: number;
}

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Morning';
  if (hour < 17) return 'Afternoon';
  return 'Evening';
}

function getDateHeadline(): string {
  return new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: 'America/Denver',
  });
}

export function TodayHeader({ userName, banner, projectTasks, checklistDone, checklistTotal }: Props) {
  const greeting = getGreeting();
  const dateHeadline = getDateHeadline();

  // Compute urgency status
  const activeTasks = projectTasks.filter((t) => t.status !== 'done');
  const overdueTasks = activeTasks.filter((t) => t.escalation === 'red');
  const isCaughtUp = activeTasks.length === 0 && checklistDone === checklistTotal && checklistTotal > 0;

  return (
    <div className="space-y-4 pb-6 border-b border-[var(--border)]">
      {/* Date + greeting */}
      <div>
        <h1
          className="font-serif font-[600] text-[40px] leading-[1.1] tracking-[-0.02em]"
          style={{ color: 'var(--color-near-black)', fontFamily: 'var(--font-serif)' }}
        >
          {dateHeadline}
        </h1>
        <p className="text-[16px] text-[var(--color-charcoal)] mt-1">
          {greeting}, {userName}.
        </p>
      </div>

      {/* Remember banner */}
      {banner && (
        <div
          className="rounded-[4px] px-5 py-4"
          style={{
            background: 'var(--color-warm-white)',
            borderLeft: '4px solid var(--color-gold)',
            border: '1px solid rgba(184,150,62,0.25)',
            borderLeftWidth: '4px',
          }}
        >
          <p
            className="italic text-[18px] leading-snug"
            style={{ fontFamily: 'var(--font-serif)', color: 'var(--color-near-black)' }}
          >
            {banner.body}
          </p>
          <p className="text-[13px] text-[var(--color-mid-gray)] mt-1">— Ken</p>
        </div>
      )}

      {/* Status line */}
      <StatusLine
        isCaughtUp={isCaughtUp}
        activeCount={activeTasks.length}
        overdueCount={overdueTasks.length}
      />
    </div>
  );
}

function StatusLine({
  isCaughtUp,
  activeCount,
  overdueCount,
}: {
  isCaughtUp: boolean;
  activeCount: number;
  overdueCount: number;
}) {
  if (isCaughtUp) {
    return (
      <p
        className="text-[15px] font-[500] relative inline-block"
        style={{ color: 'var(--color-gold-dark)' }}
        aria-live="polite"
      >
        You're caught up — great time to build a skill.
        <span
          className="absolute bottom-0 left-0 right-0 h-[2px] rounded-full"
          style={{ background: 'var(--color-gold)', opacity: 0.5 }}
          aria-hidden
        />
      </p>
    );
  }

  if (overdueCount > 0) {
    return (
      <p className="text-[15px] font-[500] text-[var(--color-charcoal)]" aria-live="polite">
        {activeCount} task{activeCount !== 1 ? 's' : ''} need attention ·{' '}
        <span style={{ color: 'var(--destructive)' }}>
          {overdueCount} overdue
        </span>
      </p>
    );
  }

  return (
    <p className="text-[15px] font-[500] text-[var(--color-charcoal)]" aria-live="polite">
      {activeCount} task{activeCount !== 1 ? 's' : ''} need attention today
    </p>
  );
}
